import { AgTechIdentityBaseURL } from "@agtech/core/auth/AgTechAuthData";
import { ApplicationDTO, Operation, OperationAddress, OperationInvitationManagementDTO, OperationLicenseDTO, PaymentConfirmationDTO, ProductDTO, StateProvinceDTO } from "./OperationData";
import { AgTechQuery, useAgTechQuery } from "@agtech/core/data/queries/AgTechDataQueries";
import AgTechQueryLoadingSurface, { AgTechQueryLoadingSurfaceSettings } from "@agtech/core/data/queries/AgTechQueryLoadingSurface";

///
/// Queries
///

export const OperationQueryKey = 'Operation';

// Info

export const OperationDataGetOperationInfoQuery: AgTechQuery<any, Operation> = {
    queryKey: () => [OperationQueryKey, 'info'],
    query: async (props) => {
        return await props.http.getEntity({
            request: {
                baseURL: AgTechIdentityBaseURL,
                path: 'Operations'
            }
        });
    },
    content: {
        loadingText: 'Loading operation information...',
        failureText: 'Failed to load operation information.'
    }
}

export const OperationDataGetOperationLogoQuery: AgTechQuery<any, string> = {
    queryKey: () => [OperationQueryKey, 'logo'],
    query: async (props) => {
        return await props.http.getImage({
            // baseURL: AgTechIdentityBaseURL,
            path: 'Operation/Logo'
        });
    },
    content: {
        loadingText: 'Loading operation logo...',
        failureText: 'Failed to load operation logo.'
    }
}

/// Addresses

export const OperationDataGetOperationAddressesQuery: AgTechQuery<any, OperationAddress[]> = {
    queryKey: () => [OperationQueryKey, 'addresses'],
    query: async (props) => {
        return await props.http.getEntity({
            request: {
                baseURL: AgTechIdentityBaseURL,
                path: 'Operations/Addresses'
            }
        });
    },
    content: {
        loadingText: 'Loading operation addresses...',
        failureText: 'Failed to load operation addresses.'
    }
}

export const OperationDataGetOperationBillingAddressQuery: AgTechQuery<any, OperationAddress> = {
    queryKey: () => [OperationQueryKey, 'addresses', 'billingAddress'],
    query: async (props) => {
        return await props.http.getEntity<OperationAddress[], OperationAddress>({
            request: {
                baseURL: AgTechIdentityBaseURL,
                path: 'Operations/Addresses?isBilling=true'
            },
            convert: (data) => data[0]
        });
    },
    content: {
        loadingText: 'Loading billing address...',
        failureText: 'Failed to load billing address.'
    }
}

export const OperationDataGetOperationStatesQuery: AgTechQuery<any, StateProvinceDTO[]> = {
    queryKey: () => [OperationQueryKey, 'states'],
    query: async (props) => {
        return await props.http.getEntity({
            request: {
                baseURL: AgTechIdentityBaseURL,
                path: 'StateProvinces'
            }
        });
    },
    content: {
        loadingText: 'Loading states...',
        failureText: 'Failed to load states.'
    }
}

/// Licenses

export const OperationDataGetOperationLicensedApplicationsQuery: AgTechQuery<any, ApplicationDTO[]> = {
    queryKey: () => [OperationQueryKey, 'applications'],
    query: async (props) => {
        return await props.http.getEntity<ApplicationDTO[]>({
            request: {
                baseURL: AgTechIdentityBaseURL,
                path: 'Operations/Applications'
            }
        });
    },
    content: {
        loadingText: 'Loading licensed applications...',
        failureText: 'Failed to load licensed applications.'
    }
}

export const OperationDataGetOperationLicenseHistoryQuery: AgTechQuery<any, OperationLicenseDTO[]> = {
    queryKey: () => [OperationQueryKey, 'licenseHistory'],
    query: async (props) => {
        return await props.http.getEntity<OperationLicenseDTO[]>({
            request: {
                baseURL: AgTechIdentityBaseURL,
                path: 'Operations/Licenses',
            }
        });
    },
    content: {
        loadingText: 'Loading license history...',
        failureText: 'Failed to load license history.'
    }
}

/// Applications

export const OperationDataGetAvailableApplicationsQuery: AgTechQuery<any, ApplicationDTO[]> = {
    queryKey: () => [OperationQueryKey, 'availableApplications'],
    query: async (props) => {
        return await props.http.getEntity<ApplicationDTO[]>({
            request: {
                baseURL: AgTechIdentityBaseURL,
                path: 'Application/Available'
            }
        });
    },
    content: {
        loadingText: 'Loading applications...',
        failureText: 'Unable to load applications. Please try again.'
    }
}

export const OperationDataGetProductsForApplicationQuery: AgTechQuery<{ appId: string }, ProductDTO[]> = {
    queryKey: (props) => [OperationQueryKey, 'applicationProducts', props.appId],
    query: async (props) => {
        return await props.http.getEntity<ProductDTO[]>({
            request: {
                baseURL: AgTechIdentityBaseURL,
                path: `Application/${props.inputData.appId}/Products`
            }
        });
    },
    content: {
        loadingText: 'Loading products...',
        failureText: 'Unable to load products. Please try again.'
    }
}

export const OperationDataGetCheckoutSessionQuery: AgTechQuery<{ sessionId: string }, PaymentConfirmationDTO> = {
    queryKey: (props) => [OperationQueryKey, 'applicationSessions', props.sessionId],
    query: async (props) => {
        return await props.http.getEntity<PaymentConfirmationDTO>({
            request: {
                baseURL: AgTechIdentityBaseURL,
                path: `Operations/Licenses/CheckoutSession/${props.inputData.sessionId}`
            }
        });
    },
    content: {
        loadingText: 'Loading session...',
        failureText: 'Unable to load session. Please try again.'
    }
}

export const OperationDataGetOutstandingInvitationsQuery: AgTechQuery<any, OperationInvitationManagementDTO[]> = {
    queryKey: () => [OperationQueryKey],
    query: async (props) => {
        return await props.http.getEntity<OperationInvitationManagementDTO[]>({
            request: {
                baseURL: AgTechIdentityBaseURL,
                path: `Operations/Invitations`
            }
        });
    },
    content: {
        loadingText: 'Loading invitations...',
        failureText: 'Unable to load invitations. Please try again.'
    }
}

///
/// Providers
///

export const OperationDataProvider = (props: AgTechQueryLoadingSurfaceSettings & {
    children: (operationData: Operation) => JSX.Element
}) => {
    const operationQuery = useAgTechQuery({
        query: OperationDataGetOperationInfoQuery
    });

    return (
        <AgTechQueryLoadingSurface query={operationQuery} {...props}>
            {props.children}
        </AgTechQueryLoadingSurface>
    );
}

export const OperationLogoProvider = (props: AgTechQueryLoadingSurfaceSettings & {
    children: (operationLogo: string) => JSX.Element
}) => {
    const operationLogoQuery = useAgTechQuery({
        query: OperationDataGetOperationLogoQuery
    });

    return (
        <AgTechQueryLoadingSurface query={operationLogoQuery} {...props}>
            {props.children}
        </AgTechQueryLoadingSurface>
    );
}
